import { Zap } from "lucide-react";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingFooter from "@/components/landing/LandingFooter";
import {
  UseCaseHero,
  UseCaseContext,
  UseCaseProblems,
  UseCaseSolution,
  UseCaseResults,
  UseCaseTestimonial,
  UseCaseCTA,
  RelatedUseCases,
} from "@/components/use-cases";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const ACCENT_COLOR = "#F59E0B";

const problems = [
  "Tickets de caisse qui s'accumulent dans la boite a gants",
  "Decouverte des pertes 3 mois apres la fin du chantier",
  "Impossible de savoir quel chantier est vraiment rentable",
  "Temps perdu a ressaisir les factures dans Excel",
];

const solutions = [
  {
    title: "Import Scan IA des tickets",
    description: "Photo → donnees extraites automatiquement",
  },
  {
    title: "Suivi cout main d'oeuvre",
    description: "Son temps + celui de l'apprenti, calcules au reel",
  },
  {
    title: "Alertes depassement",
    description: "Notification des que le budget materiel derape",
  },
  {
    title: "Simulateur retards",
    description: "Impact financier si un chantier prend du retard",
  },
];

const results = [
  {
    metric: "Marge moyenne",
    before: "15%",
    after: "22% (+7 pts)",
  },
  {
    metric: "Chantiers deficitaires",
    before: "2-3/an",
    after: "0 sur 8 mois",
  },
  {
    metric: "Temps admin/semaine",
    before: "5h",
    after: "2h",
  },
];

const Electricien = () => {
  usePageMeta({
    title: "Logiciel gestion chantier électricien — B8ild",
    description: "Suivi de rentabilité pour électriciens. Gérez vos chantiers PAC, IRVE, rénovation énergétique. Marges en temps réel avec B8ild.",
    url: "https://b8ild.fr/use-cases/electricien",
  });

  return (
    <div className="min-h-screen bg-[#0A0A0F] overflow-x-hidden">
      <LandingHeader />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Cas d'usage", item: "https://b8ild.fr/use-cases" },
          { "@type": "ListItem", position: 3, name: "Électricien", item: "https://b8ild.fr/use-cases/electricien" },
        ],
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Logiciel gestion chantier électricien — B8ild",
        description: "Suivi de rentabilité pour électriciens. Gérez vos chantiers PAC, IRVE, rénovation énergétique. Marges en temps réel avec B8ild.",
        url: "https://b8ild.fr/use-cases/electricien",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
        inLanguage: "fr",
      }} />

      <UseCaseHero
        badge="Electricien"
        title="Jean-Marc gere 5 chantiers"
        subtitle="en meme temps. Et sa rentabilite."
        description="Decouvrez comment B8ild aide les electriciens independants a ne plus perdre d'argent sur leurs chantiers."
        icon={Zap}
        accentColor={ACCENT_COLOR}
        personaInitials="JM"
        personaName="Jean-Marc"
        personaRole="Electricien independant + 1 apprenti"
        personaCity="Lyon · CA ~180 000 EUR/an"
      />

      <UseCaseContext
        context="Jean-Marc est electricien depuis 20 ans. Il gere 5 a 6 chantiers simultanement : des renovations completes, des mises aux normes, quelques petits depannages. Avec son apprenti, il jongle entre les devis, les achats de materiel, et les interventions urgentes."
        accentColor={ACCENT_COLOR}
      />

      <UseCaseProblems
        problems={problems}
        accentColor={ACCENT_COLOR}
      />

      <UseCaseSolution
        solutions={solutions}
        accentColor={ACCENT_COLOR}
      />

      <UseCaseResults
        results={results}
        accentColor={ACCENT_COLOR}
        period="3 mois"
      />

      <UseCaseTestimonial
        quote="Avant B8ild, je decouvrais que j'avais perdu de l'argent quand mon comptable me faisait le bilan. Maintenant, je vois en temps reel si ca derape. J'ai economise 3 000 EUR sur mon dernier gros chantier juste en ajustant mes achats."
        name="Jean-Marc"
        role="Electricien"
        city="Lyon"
        initials="JM"
        accentColor={ACCENT_COLOR}
      />

      {/* Spécificités métier */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Vos chantiers, on les connaît
            </h2>
            <p className="text-white/60 leading-relaxed">
              Installation PAC, bornes IRVE, mise aux normes NFC 15-100, tableau électrique, domotique, photovoltaïque — B8ild gère tous ces types de chantiers. Vous bossez en rénovation énergétique ? Les aides MaPrimeRénov', primes CEE et TVA 5.5% changent vos marges. B8ild vous aide à les intégrer dans vos calculs pour savoir ce que vous gagnez vraiment.
            </p>
          </div>
        </div>
      </section>

      {/* Cas concret */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Cas concret : 3 IRVE + 2 rénovations — quand un retard fournisseur mange 3 chantiers
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Jean-Marc a 3 chantiers de bornes IRVE en cours (entre 4 000€ et 8 000€ chacun) et 2 rénovations complètes (25 000€ et 32 000€). Son fournisseur de bornes Schneider a 15 jours de retard. Les bornes ne sont pas livrées, mais ses 2 ouvriers sont planifiés sur ces chantiers. Il les envoie en renfort sur les rénovations — sauf que là-bas, les plaquistes n'ont pas fini. Résultat : 2 ouvriers payés à ne rien faire pendant 3 jours. Coût : 2 × 180€ × 3 = 1 080€ de main d'œuvre perdue.
              </p>
              <p>
                Ce qu'il ne voit pas : le retard IRVE décale aussi la facturation. Les 3 chantiers IRVE représentent 18 000€ de CA. 15 jours de retard = 15 jours de trésorerie en moins. Son découvert bancaire passe de 2 000€ à 6 000€. Les agios : 45€/mois.
              </p>
              <p>
                Avec B8ild, Jean-Marc aurait vu dès le jour 2 du retard : l'impact sur sa trésorerie, le coût des ouvriers inoccupés, et le décalage sur ses 2 autres chantiers IRVE. Il aurait pu réaffecter ses ouvriers sur un dépannage urgent facturé 85€/h au lieu de les laisser attendre.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions métier */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions fréquentes — Électriciens
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Combien coûte un électricien à l'heure, charges comprises ?",
                  a: "Un électricien N3P2 à 2 400€ brut coûte environ 3 800€/mois charges comprises (coefficient 1.58). Sur 150 heures productives par mois, ça fait 25,30€/h de coût réel. Les taux horaires facturés vont de 45€ à 80€/h selon la région et la spécialité. En IRVE, comptez 55-75€/h. En dépannage : 65-90€/h.",
                },
                {
                  q: "Comment gérer les aides MaPrimeRénov' dans ses devis ?",
                  a: "MaPrimeRénov' est versée au client, pas à l'artisan. Mais elle impacte votre prix de vente réel. Si le client touche 4 000€ d'aide sur un chantier à 12 000€, il ne paie que 8 000€. Vos coûts restent les mêmes. B8ild sépare le montant HT, la prime client, et le reste à charge pour que vous voyiez votre marge réelle — pas la marge sur le prix gonflé.",
                },
                {
                  q: "Quel est le taux horaire moyen d'un électricien en 2026 ?",
                  a: "Entre 45€ et 80€/h HT selon la spécialité. Dépannage : 65-90€/h. Installation courante : 45-55€/h. IRVE (bornes de recharge) : 55-75€/h. Photovoltaïque : 50-70€/h. Ces tarifs varient de 15-20% selon la région — plus cher en Île-de-France et PACA.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <UseCaseCTA accentColor={ACCENT_COLOR} />

      <RelatedUseCases currentSlug="electricien" />

      <LandingFooter />
    </div>
  );
};

export default Electricien;
